// What every Ask CL stream request tells the coach about where the asker is:
// their timezone (for "today", due dates) and the page they are on (for
// "this goal"-style questions). Used by every reader of the stream endpoint.

// Ask CL caps the "where am I" hint at this length.
const MAX_PAGE_CONTEXT_LENGTH = 200
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The asker's IANA timezone (e.g. "America/Chicago"), when the browser knows it. */
export const browserTimezone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

// A path segment naming a record (uuid, number, long token) rather than a page.
const isIdSegment = (segment: string) =>
  /^\d+$/.test(segment) ||
  UUID_SEGMENT.test(segment) ||
  (segment.length >= 16 && /\d/.test(segment))

/**
 * Where the user is in the app, for the coach: the route without the tenant
 * and record ids (e.g. "Page: /owner-v2/goals/game-plan").
 */
export const describePage = (pathname: string, tenantId?: string): string | undefined => {
  const segments = pathname
    .split('/')
    .filter((segment) => segment && segment !== tenantId && !isIdSegment(segment))
  return segments.length
    ? `Page: /${segments.join('/')}`.slice(0, MAX_PAGE_CONTEXT_LENGTH)
    : undefined
}

/** The `timezone` and `pageContext` fields of a stream request body (left out when unknown). */
export const streamContextFields = (
  pathname: string,
  tenantId?: string
): { timezone?: string; pageContext?: string } => {
  const timezone = browserTimezone()
  const pageContext = describePage(pathname, tenantId)
  return {
    ...(timezone && { timezone }),
    ...(pageContext && { pageContext }),
  }
}
